import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn, Router } from '@angular/router';
import Swal from 'sweetalert2';

export type AppRole = 'ADMIN' | 'DOCTOR';

const HOME: Record<AppRole, string> = {
  ADMIN: '/admin/dashboard',
  DOCTOR: '/doctor/patient-management'
};

// Logged-in user from the login response saved in user_details, or null.
// Any role other than DOCTOR uses the admin pages.
export function currentRole(): AppRole | null {
  try {
    const session = JSON.parse(localStorage.getItem('user_details') || 'null');
    if (!session?.tokens?.accessToken) {
      return null;
    }
    return String(session?.user?.roleName || '').toUpperCase() === 'DOCTOR' ? 'DOCTOR' : 'ADMIN';
  } catch {
    return null;
  }
}

// home page of the logged-in user, or the login page
export function currentHome(): string {
  const role = currentRole();
  return role ? HOME[role] : '/login';
}

function showMessage(title: string, text: string): void {
  Swal.fire({ icon: 'warning', title, text, confirmButtonColor: '#0f3e6d' });
}

// Allows the route only for the given role: no session -> login page,
// the other role -> that role's home page, each with a message.
export function roleGuard(role: AppRole): CanActivateFn {
  return () => {
    // the server render (prerender) has no localStorage; the browser runs the check
    if (!isPlatformBrowser(inject(PLATFORM_ID))) {
      return true;
    }

    const router = inject(Router);
    const userRole = currentRole();
    if (!userRole) {
      showMessage('Please log in', 'You need to log in to open that page.');
      return router.parseUrl('/login');
    }
    if (userRole !== role) {
      showMessage('Access denied', 'You don\'t have permission to open that page, so you have been taken to your home page.');
      return router.parseUrl(HOME[userRole]);
    }
    return true;
  };
}
